import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, Check, Mail, MessageSquare, Phone, Smartphone, MailOpen, Info } from "lucide-react";

/**
 * MobileNotifications — patients see their incoming in-app notifications
 * AND control their multi-channel opt-out preferences (Email / SMS / Push /
 * Phone Call / Postal Mail). Contact info displayed at the top per spec.
 */
const CHANNELS = [
  { key: "email",  label: "Email",             icon: Mail,         status: "live" },
  { key: "sms",    label: "Text Message",      icon: MessageSquare, status: "coming_soon" },
  { key: "push",   label: "Push Notification", icon: Smartphone,    status: "coming_soon" },
  { key: "phone",  label: "Phone Call",        icon: Phone,         status: "coming_soon" },
  { key: "postal", label: "Postal Mail",       icon: MailOpen,      status: "coming_soon" },
];

export default function MobileNotifications() {
  const [notifs, setNotifs]   = useState(null);
  const [prefs, setPrefs]     = useState(null);
  const [contact, setContact] = useState(null);
  const [busy, setBusy]       = useState(false);

  const loadNotifs = () => api.get("/patient/notifications").then((r) => setNotifs(r.data));
  const loadPrefs  = () => api.get("/patient/notification-prefs").then((r) => { setPrefs(r.data.prefs); setContact(r.data.contact); });

  useEffect(() => { loadNotifs(); loadPrefs(); }, []);

  const toggle = async (key) => {
    if (busy) return;
    setBusy(true);
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); // optimistic
    try { await api.patch("/patient/notification-prefs", { [key]: !prefs[key] }); }
    finally { setBusy(false); }
  };

  const markRead = async (id) => {
    await api.post(`/patient/notifications/${id}/read`);
    loadNotifs();
  };
  const markAllRead = async () => {
    await api.post("/patient/notifications/read-all");
    loadNotifs();
  };

  if (!prefs) return <div className="max-w-md mx-auto p-6 text-stone-500">Loading…</div>;

  const unread = (notifs || []).filter((n) => !n.read).length;

  return (
    <div className="max-w-md mx-auto pb-4" data-testid="mobile-notifications">
      <header className="px-5 pt-6 pb-3">
        <h1 className="font-display text-2xl font-bold text-stone-900">Notifications</h1>
        <p className="text-stone-500 text-sm">Manage how your care team reaches you.</p>
      </header>

      {/* Patient contact info (per spec — visible at the TOP of the screen) */}
      {contact && (
        <section className="px-5 mb-4">
          <div className="bg-white border border-stone-200 rounded-2xl p-4 shadow-soft" data-testid="contact-info">
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2 flex items-center gap-1"><Info className="w-3.5 h-3.5" />Your Contact Information</div>
            <div className="text-sm text-stone-700 space-y-0.5">
              <div><span className="text-stone-500">Email:</span> {contact.email || "—"}</div>
              <div><span className="text-stone-500">Phone:</span> {contact.phone || "—"}</div>
              <div><span className="text-stone-500">Address:</span> {[contact.address, contact.city, contact.state, contact.zip].filter(Boolean).join(", ") || "—"}</div>
            </div>
          </div>
        </section>
      )}

      {/* Channel opt-outs */}
      <section className="px-5 mb-6" data-testid="channels-section">
        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Delivery Channels</div>
        <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100">
          {CHANNELS.map((c) => (
            <div key={c.key} className="flex items-center gap-3 px-4 py-3">
              <div className="w-9 h-9 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center"><c.icon className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <div className="font-medium text-stone-900 text-sm">{c.label}</div>
                  {c.status === "coming_soon" && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full" data-testid={`channel-soon-${c.key}`}>Coming soon</span>
                  )}
                </div>
                <div className="text-xs text-stone-500">
                  {c.status === "coming_soon"
                    ? "Toggle saved — delivery wires up when this channel goes live."
                    : (prefs[c.key] ? "Enabled" : "Opted out")}
                </div>
              </div>
              <button
                role="switch" aria-checked={!!prefs[c.key]}
                onClick={() => toggle(c.key)}
                disabled={busy}
                data-testid={`channel-toggle-${c.key}`}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${prefs[c.key] ? "bg-emerald-500" : "bg-stone-300"}`}
              >
                <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${prefs[c.key] ? "translate-x-5" : "translate-x-0"}`} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* In-app notifications feed */}
      <section className="px-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Inbox</div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs font-semibold text-brand-700" data-testid="mark-all-read">Mark all read</button>
          )}
        </div>
        <div className="space-y-2.5">
          {(notifs || []).length === 0 && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5 text-center text-stone-500 text-sm">
              No notifications yet.
            </div>
          )}
          {(notifs || []).map((n) => (
            <button key={n.id} onClick={() => markRead(n.id)} className={`w-full text-left bg-white border rounded-2xl p-4 ${n.read ? "border-stone-200" : "border-amber-300 shadow-soft"}`} data-testid={`notif-${n.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${n.read ? "bg-stone-100 text-stone-500" : "bg-amber-100 text-amber-700"}`}>
                  {n.read ? <Check className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-stone-900 text-sm">{n.title}</div>
                  <div className="text-xs text-stone-600 mt-0.5 line-clamp-3">{n.body}</div>
                  <div className="text-[10px] text-stone-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
